import { useEffect, useMemo, useState } from "react";
import { EAccessLevel } from "../../../../../shared/oversight-core/enums/access-level";
import { EAccessScope } from "../../../../../shared/oversight-core/enums/access-scope";
import { IAccessTarget } from "../../../../../shared/oversight-core/interfaces/access-target";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import {
  accessScopeLabels,
  getAccessLevelInfo,
} from "../../../../../shared/oversight-core/utils/get-org-user-info";
import styles from "./access-target-tree.module.scss";

interface ITreeNode {
  target: IAccessTarget;
  children: ITreeNode[];
  siteCount: number;
}

interface IProps {
  targets: IAccessTarget[];
  // Level the user already holds on each target, keyed by target identity.
  grantedLevels: Map<string, EAccessLevel>;
  searchText: string;
  selectedId?: string;
  onSelect: (target: IAccessTarget) => void;
}

// Builds the tree from parentIdentity, which is null at the root.
const buildTree = (
  targets: IAccessTarget[],
  parentId: string | null,
): ITreeNode[] =>
  targets
    .filter((t) => (t.parentIdentity ?? null) === parentId)
    .map((target) => {
      const children = buildTree(targets, target.identity);
      return {
        target,
        children,
        siteCount: children.reduce(
          (count, child) =>
            count +
            (child.target.scope === EAccessScope.SITE ? 1 : child.siteCount),
          0,
        ),
      };
    });

const AccessTargetTree = (props: IProps) => {
  const { targets, grantedLevels, searchText, selectedId, onSelect } = props;
  const tree = useMemo(() => buildTree(targets, null), [targets]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // The tree arrives after the first render, so the organization root opens once it does.
  useEffect(() => {
    setExpandedIds((ps) => {
      const next = new Set(ps);
      tree.forEach((node) => next.add(node.target.identity));
      return next;
    });
  }, [tree]);

  const query = searchText.trim().toLowerCase();
  const isSearching = query.length > 0;

  const matches = (node: ITreeNode): boolean =>
    node.target.scopeName.toLowerCase().includes(query) ||
    node.children.some(matches);

  const toggle = (id: string) =>
    setExpandedIds((ps) => {
      const next = new Set(ps);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // While searching, every branch on the way to a match is open, and a
  // matching branch shows everything under it.
  const renderNodes = (
    nodes: ITreeNode[],
    depth: number,
    showAll: boolean,
  ): JSX.Element[] =>
    nodes
      .filter((node) => !isSearching || showAll || matches(node))
      .flatMap((node) => {
        const { target, children } = node;
        const grantedLevel = grantedLevels.get(target.identity);
        const isSelectable = !grantedLevel && !target.isRetired;
        const isSelected = target.identity === selectedId;
        const hasChildren = children.length > 0;
        const isExpanded = isSearching || expandedIds.has(target.identity);
        const isMatch =
          isSearching && target.scopeName.toLowerCase().includes(query);

        const row = (
          <div
            key={target.identity}
            role="treeitem"
            aria-selected={isSelected}
            aria-expanded={hasChildren ? isExpanded : undefined}
            tabIndex={0}
            className={`${styles.treeRow} ${
              isSelected ? styles.selected : ""
            } ${target.isRetired ? styles.retired : ""} ${
              isSelectable || hasChildren ? "cursor-pointer" : ""
            }`}
            style={{ paddingLeft: 16 + depth * 20 }}
            onClick={() => {
              if (isSelectable) onSelect(target);
              else if (hasChildren) toggle(target.identity);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          >
            <div className="d-flex align-items-center gap-2 overflow-hidden">
              {hasChildren && (
                <MaterialIcon
                  icon="chevron_right"
                  size={18}
                  className={`${styles.chevron} ${
                    isExpanded ? styles.expanded : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(target.identity);
                  }}
                />
              )}
              {isSelected && (
                <MaterialIcon icon="check" size={18} color="#2f2a89" />
              )}
              <span
                className={`${styles.name} font-size-13 text-truncate ${
                  hasChildren || isSelected ? "font-weight-600" : ""
                }`}
              >
                {target.scopeName}
              </span>
              <span className={`${styles.tag} font-size-11`}>
                {accessScopeLabels[target.scope]}
              </span>
            </div>
            <div className="text-light font-size-12 text-nowrap">
              {grantedLevel ? (
                <>
                  Already granted ·{" "}
                  <span className="text-primary font-weight-500">
                    {getAccessLevelInfo(grantedLevel).label}
                  </span>
                </>
              ) : target.isRetired ? (
                "Retired · can't be granted"
              ) : hasChildren ? (
                `${node.siteCount} site${node.siteCount === 1 ? "" : "s"}`
              ) : null}
            </div>
          </div>
        );

        return hasChildren && isExpanded
          ? [row, ...renderNodes(children, depth + 1, showAll || isMatch)]
          : [row];
      });

  const rows = renderNodes(tree, 0, false);

  return (
    <div role="tree" className={styles.tree}>
      {rows.length > 0 ? (
        rows
      ) : (
        <div className="text-center text-light font-size-12 py-4">
          No branches or sites match your search.
        </div>
      )}
    </div>
  );
};

export default AccessTargetTree;
